import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { subscribeToStreamOutputStateMessages } from "./AppContext";
import { StreamStateValue } from "../types/streamState";

type OutputStateDetails = {
  state: StreamStateValue;
  message?: string;
};

type StreamOutputsMap = Record<string, Record<string, OutputStateDetails>>;

type StreamOutputsStateContextValue = {
  outputStates: StreamOutputsMap;
  getOutputState: (streamId: string | null | undefined, outputId: string | null | undefined) => StreamStateValue;
  getOutputStateMessage: (streamId: string | null | undefined, outputId: string | null | undefined) => string | null;
};

const snapshotEventType = "streamOutputStateSnapshot";
const changedEventType = "streamOutputStateChanged";

type SnapshotPayload = {
  states?: Record<string, unknown> | null;
};

type UpdatePayload = {
  streamId?: unknown;
  outputId?: unknown;
  state?: unknown;
  message?: unknown;
};

const StreamOutputsStateContext = createContext<StreamOutputsStateContextValue | undefined>(undefined);

const isStreamStateValue = (value: string): value is StreamStateValue => {
  const normalized = value.trim().toLowerCase();
  return (
    normalized === "online" ||
    normalized === "offline" ||
    normalized === "timeout" ||
    normalized === "unknown" ||
    normalized === "connecting" ||
    normalized === "error"
  );
};

const parseState = (value: unknown): StreamStateValue | null => {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (isStreamStateValue(normalized)) {
    return normalized;
  }
  return null;
};

const parseMessage = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const parseSnapshotEntry = (value: unknown): OutputStateDetails | null => {
  if (typeof value === "string") {
    const state = parseState(value);
    return state ? { state } : null;
  }
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as { state?: unknown; message?: unknown };
  const state = parseState(candidate.state);
  if (!state) {
    return null;
  }

  const message = parseMessage(candidate.message);
  return message ? { state, message } : { state };
};

export const StreamOutputsStateProvider = ({ children }: { children: ReactNode }) => {
  const [outputStates, setOutputStates] = useState<StreamOutputsMap>({});

  useEffect(() => {
    const handleSnapshot = (payload: SnapshotPayload) => {
      try {
        const streams = payload?.states ?? {};
        const nextStates: StreamOutputsMap = {};
        if (streams && typeof streams === "object") {
          for (const [rawStreamId, outputs] of Object.entries(streams)) {
            const streamId = rawStreamId.trim();
            if (!streamId || !outputs || typeof outputs !== "object") {
              continue;
            }
            const outputEntries: Record<string, OutputStateDetails> = {};
            for (const [rawOutputId, stateValue] of Object.entries(outputs as Record<string, unknown>)) {
              const outputId = rawOutputId.trim();
              if (!outputId) {
                continue;
              }
              const details = parseSnapshotEntry(stateValue);
              if (!details) {
                continue;
              }
              outputEntries[outputId] = details;
            }
            if (Object.keys(outputEntries).length > 0) {
              nextStates[streamId] = outputEntries;
            }
          }
        }
        setOutputStates(nextStates);
      } catch (error) {
        console.error("Failed to parse stream output state snapshot", error);
      }
    };

    const handleUpdate = (payload: UpdatePayload) => {
      try {
        const rawStreamId = payload?.streamId ?? "";
        const rawOutputId = payload?.outputId ?? "";
        const streamId = typeof rawStreamId === "string" ? rawStreamId.trim() : "";
        const outputId = typeof rawOutputId === "string" ? rawOutputId.trim() : "";
        const state = parseState(payload?.state);
        if (!streamId || !outputId || !state) {
          return;
        }

        const message = parseMessage(payload?.message);

        setOutputStates((previous) => {
          const next: StreamOutputsMap = { ...previous };
          const outputs = { ...(previous[streamId] ?? {}) };
          const current = outputs[outputId];
          const nextDetails: OutputStateDetails = message ? { state, message } : { state };

          if (current && current.state === nextDetails.state && current.message === nextDetails.message) {
            return previous;
          }

          outputs[outputId] = nextDetails;
          next[streamId] = outputs;
          return next;
        });
      } catch (error) {
        console.error("Failed to parse stream output state update", error);
      }
    };

    const handleMessage = (message: { type: string; data: unknown }) => {
      if (message.type === snapshotEventType) {
        handleSnapshot(message.data as SnapshotPayload);
      } else if (message.type === changedEventType) {
        handleUpdate(message.data as UpdatePayload);
      }
    };

    const unsubscribe = subscribeToStreamOutputStateMessages(handleMessage);
    return unsubscribe;
  }, []);

  const value = useMemo<StreamOutputsStateContextValue>(
    () => ({
      outputStates,
      getOutputState: (streamId, outputId) => {
        const s = streamId?.trim();
        const o = outputId?.trim();
        if (!s || !o) {
          return "unknown";
        }
        return outputStates[s]?.[o]?.state ?? "unknown";
      },
      getOutputStateMessage: (streamId, outputId) => {
        const s = streamId?.trim();
        const o = outputId?.trim();
        if (!s || !o) {
          return null;
        }
        return outputStates[s]?.[o]?.message ?? null;
      }
    }),
    [outputStates]
  );

  return <StreamOutputsStateContext.Provider value={value}>{children}</StreamOutputsStateContext.Provider>;
};

export const useStreamOutputsState = () => {
  const context = useContext(StreamOutputsStateContext);
  if (!context) {
    throw new Error("useStreamOutputsState must be used within a StreamOutputsStateProvider");
  }
  return context;
};
